'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Description,
  Assessment,
  Campaign,
  School,
  Inventory,
  ExitToApp,
  Hub,
  VerifiedUser,
  CheckCircle,
  WarningAmber,
  ArrowForward,
  ExpandMore,
} from '@mui/icons-material';
import Header from '../../components/HomepageContent/Header';
import FeatureHeroCtaStack from '../components/FeatureHeroCtaStack';
import FeatureResourceButtons from '../components/FeatureResourceButtons';
import Footer from '../../components/HomepageContent/Footer';
import BrandName from '../../components/BrandName';
import WorkflowStepper from '../../components/WorkflowStepper';
import Link from 'next/link';
import '../features-responsive.css';

const BRAND_BLUE = '#0F4C9C';
const BRAND_GREEN = '#63A821';

const workflowSteps = [
  {
    title: 'Assess',
    description:
      'Conduct comprehensive all-hazards risk assessments tailored to your facility\u2019s location, resident population, and vulnerabilities.',
  },
  {
    title: 'Plan',
    description:
      'Maintain a living Emergency Operations Plan with version control, annual review tracking, and clear role assignments.',
  },
  {
    title: 'Communicate',
    description:
      'Manage communication plans for staff, residents, families, and external partners with documented contact trees and escalation protocols.',
  },
  {
    title: 'Train & Drill',
    description:
      'Schedule and track required training and drills with structured after-action reporting and improvement tracking.',
  },
  {
    title: 'Respond & Recover',
    description: 'Coordinate real-time response and document actions with full audit trails.',
  },
  {
    title: 'Improve',
    description:
      'Link findings and after-action reports directly into QAPI and Action Plans for continuous improvement.',
  },
];

const painPoints = [
  'Outdated Emergency Operations Plans that are rarely reviewed or updated',
  'Inconsistent or poorly documented risk assessments',
  'Fragmented communication plans with no clear contact trees or escalation paths',
  'Training and drills that feel like checking a box rather than building real capability',
  'Weak documentation that fails to demonstrate readiness during CMS or state surveys',
  'No clear connection between emergency response and ongoing QAPI improvement',
];

const capabilities = [
  {
    icon: Description,
    title: 'Living Emergency Operations Plan',
    description:
      'Maintain a dynamic Emergency Operations Plan with version control, automated annual review reminders, and clear approval workflows. No more outdated binders.',
  },
  {
    icon: Assessment,
    title: 'All-Hazards Risk Assessment',
    description:
      'Conduct structured risk assessments covering natural, technological, and human-caused hazards specific to your facility and geographic location.',
  },
  {
    icon: Campaign,
    title: 'Communication Planning',
    description:
      'Manage comprehensive communication plans including staff notification trees, resident and family notification procedures, and coordination with external partners (hospitals, EMS, public health, vendors).',
  },
  {
    icon: School,
    title: 'Training & Drill Management with After-Action Reporting',
    description:
      'Schedule, track, and document all required training and drills. Capture structured after-action reports with identified gaps and improvement actions.',
  },
  {
    icon: Inventory,
    title: 'Resource & Vendor Management',
    description:
      'Track critical resources, emergency supplies, vendor agreements, and mutual aid arrangements with expiration alerts and contact information.',
  },
  {
    icon: ExitToApp,
    title: 'Evacuation Planning & Resident Tracking',
    description:
      'Maintain evacuation plans by unit and track resident movement, transportation needs, and destination facilities during emergencies.',
  },
  {
    icon: Hub,
    title: 'QAPI Integration',
    description:
      'Automatically link after-action reports, drill findings, and emergency events into your QAPI program and Action Plans for continuous improvement.',
  },
  {
    icon: VerifiedUser,
    title: 'Audit-Ready Documentation',
    description:
      'Generate organized, timestamped documentation packages aligned with CMS Emergency Preparedness requirements (42 CFR \u00A7483.73) for surveys and audits.',
  },
];

const roles = [
  {
    title: 'Administrators & DONs',
    benefit:
      'Full visibility into plan status, training completion, drill performance, and regulatory readiness',
  },
  {
    title: 'Emergency Preparedness Coordinators',
    benefit:
      'Structured workflows for plan maintenance, risk assessment, drill scheduling, and documentation',
  },
  {
    title: 'Department Heads',
    benefit: 'Clear roles, responsibilities, and training requirements during emergencies',
  },
  {
    title: 'Corporate / Multi-Site Operators',
    benefit:
      'Portfolio-wide visibility into emergency preparedness status, training completion, and drill performance across all facilities',
  },
];

const outcomes = [
  'Always current, version-controlled Emergency Operations Plan',
  'Documented, facility-specific risk assessments',
  'Consistent staff training and meaningful drill participation',
  'Clear communication protocols with documented contact trees',
  'Reduced regulatory risk during CMS and state surveys',
  'Better coordination between emergency response and ongoing quality improvement',
  'Portfolio-wide visibility for multi-facility operators',
];

const integrations = [
  'Incident Investigation',
  'QAPI / Performance Improvement Plans',
  'Action Plans',
  'Document Vault',
  'Committee & Meeting Management',
];

const complianceItems = [
  'A complete, dated, version-controlled Emergency Operations Plan',
  'Documented all-hazards risk assessments',
  'Training and drill records with after-action reports and improvement actions',
  'Communication plans with clear escalation paths',
  'Resource and vendor tracking with expiration management',
  'Full audit trails aligned with CMS requirements (42 CFR \u00A7483.73)',
];

const faqs = [
  {
    q: 'Can we maintain a living Emergency Operations Plan instead of static binders?',
    a: 'Yes. The system supports version control, automated review reminders, and approval workflows.',
  },
  {
    q: 'Does it help with CMS Emergency Preparedness requirements?',
    a: 'Yes. The platform is designed around 42 CFR \u00A7483.73 requirements and generates audit-ready documentation.',
  },
  {
    q: 'Can we track training and drills with after-action reports?',
    a: 'Yes. The system supports scheduling, tracking, and structured after-action reporting with improvement actions.',
  },
  {
    q: 'Can corporate see emergency preparedness status across all facilities?',
    a: 'Yes. Leadership has portfolio-wide visibility into plan status, training completion, and drill performance.',
  },
  {
    q: 'Is Emergency Preparedness connected to our QAPI program?',
    a: 'Yes. After-action reports and drill findings can be directly linked to QAPI and Action Plans.',
  },
];

export default function EmergencyPreparednessClient() {
  const [expanded, setExpanded] = useState<number | false>(0);

  const handleChange = (panel: number) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    document.title =
      'Emergency Preparedness Software for Skilled Nursing Facilities | CareAscendium';
  }, []);

  return (
    <Box
      className="feature-page-wrapper"
      sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}
    >
      <Header />

      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1E4A8C 50%, #2A5A9C 100%)`,
          py: { xs: 6, md: 8 },
          px: 4,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="Emergency Preparedness"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              mb: 3,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3.25rem' },
              lineHeight: 1.15,
              maxWidth: 940,
              mx: 'auto',
            }}
          >
            One Living Plan. Every Scenario. Always Ready.
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.92)',
              maxWidth: 840,
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.05rem', md: '1.35rem' },
              mb: 5,
            }}
          >
            <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> Emergency Preparedness
            transforms emergency management from a compliance checkbox into a dynamic, integrated
            system. It helps facilities maintain a living Emergency Operations Plan, conduct
            meaningful risk assessments, coordinate communication, train staff effectively, and
            document every step with audit-ready records.
          </Typography>
          <FeatureHeroCtaStack>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#55901d' },
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
            <FeatureResourceButtons />
          </FeatureHeroCtaStack>
        </Container>
      </Box>

      {/* Challenge */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={5} alignItems="center" justifyContent="center">
          <Grid size={{ xs: 12, md: 10 }} sx={{ textAlign: 'center' }}>
            <Typography
              variant="h2"
              sx={{
                color: BRAND_BLUE,
                mb: 3,
                fontWeight: 700,
                fontSize: { xs: '1.75rem', md: '2.25rem' },
              }}
            >
              The Challenge
            </Typography>
            <Typography
              sx={{
                color: '#666',
                mb: 4,
                lineHeight: 1.7,
                fontSize: '1.05rem',
                maxWidth: 850,
                mx: 'auto',
              }}
            >
              Most skilled nursing facilities struggle with:
            </Typography>
            <Box
              component="ul"
              sx={{ pl: 0, listStyle: 'none', m: 0, display: 'inline-block', textAlign: 'left' }}
            >
              {painPoints.map((point) => (
                <Box
                  component="li"
                  key={point}
                  sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, gap: 1.5 }}
                >
                  <WarningAmber sx={{ color: '#FF9800', mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ color: '#555', lineHeight: 1.5 }}>{point}</Typography>
                </Box>
              ))}
            </Box>
            <Typography
              sx={{
                color: '#666',
                mt: 4,
                lineHeight: 1.7,
                fontSize: '1.05rem',
                maxWidth: 850,
                mx: 'auto',
              }}
            >
              The result is increased regulatory risk, staff confusion during actual emergencies, and
              potential harm to residents.
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #F3F1FF 0%, #ffffff 100%)',
                borderLeft: `4px solid ${BRAND_BLUE}`,
              }}
            >
              <Typography
                sx={{
                  fontStyle: 'italic',
                  color: BRAND_BLUE,
                  fontSize: '1.15rem',
                  lineHeight: 1.6,
                  fontWeight: 500,
                }}
              >
                &ldquo;A static emergency plan that sits on a shelf is not preparedness — it is a
                liability waiting to be exposed.&rdquo;
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Workflow */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
              One Plan. From Risk Assessment to Recovery.
            </Typography>
            <Typography
              sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem' }}
            >
              <BrandName variant="light" style={{ fontSize: 'inherit' }} /> Emergency Preparedness
              brings structure, accountability, and continuous improvement to the entire emergency
              management lifecycle.
            </Typography>
          </Box>
          <WorkflowStepper steps={workflowSteps} />
        </Container>
      </Box>

      {/* Capabilities */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Key Capabilities
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: 3,
            alignItems: 'stretch',
          }}
        >
          {capabilities.map((cap) => (
            <Box key={cap.title}>
              <Paper
                elevation={3}
                sx={{
                  p: 3,
                  height: '100%',
                  borderRadius: 3,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 28px rgba(21,60,127,0.12)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    bgcolor: `${BRAND_BLUE}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                  }}
                >
                  <cap.icon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
                </Box>
                <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 700, mb: 1.5 }}>
                  {cap.title}
                </Typography>
                <Typography sx={{ color: '#666', mb: 2, lineHeight: 1.6, fontSize: '0.95rem' }}>
                  {cap.description}
                </Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Roles + Platform Integration */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} justifyContent="center" alignItems="stretch">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography
                variant="h2"
                sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: '1.75rem' }}
              >
                Built for Every Role
              </Typography>
              {roles.map((role) => (
                <Box key={role.title} sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
                  <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: BRAND_BLUE }}>{role.title}</Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {role.benefit}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  height: '100%',
                  width: '100%',
                  background: `linear-gradient(145deg, ${BRAND_BLUE} 0%, #1E4A8C 100%)`,
                  color: 'white',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, fontSize: '1.5rem' }}>
                  Platform Integration
                </Typography>
                <Typography sx={{ color: '#FFFFFF', fontWeight: 500, mb: 3, lineHeight: 1.6 }}>
                  Emergency Preparedness connects seamlessly with:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, justifyContent: 'center' }}>
                  {integrations.map((item) => (
                    <Chip
                      key={item}
                      label={item}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        color: 'white',
                        fontWeight: 600,
                        border: '1px solid rgba(255,255,255,0.25)',
                      }}
                    />
                  ))}
                </Box>
                <Typography sx={{ fontWeight: 600, color: '#a8d86a', fontStyle: 'italic' }}>
                  One platform. One source of truth. Always ready.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Outcomes */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Outcomes You Can Measure
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2,
            maxWidth: 720,
            mx: 'auto',
          }}
        >
          {outcomes.map((item) => (
            <Box
              key={item}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                width: '100%',
              }}
            >
              <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
              <Typography
                sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5, textAlign: 'left' }}
              >
                {item}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Documentation */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="md">
          <Paper elevation={3} sx={{ p: { xs: 4, md: 5 }, borderRadius: 3, borderTop: `6px solid ${BRAND_BLUE}` }}>
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
                Documentation You Can Stand Behind
              </Typography>
              <Typography
                sx={{
                  color: '#666',
                  maxWidth: 780,
                  mx: 'auto',
                  lineHeight: 1.7,
                  fontSize: '1.05rem',
                }}
              >
                <BrandName variant="light" style={{ fontSize: 'inherit' }} /> supports the regulatory
                discipline emergency preparedness demands:
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'left', width: 'fit-content', mx: 'auto' }}>
              {complianceItems.map((item) => (
                <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                  <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
                  <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* FAQ */}
      <Box
        sx={{
          py: { xs: 10, md: 14 },
          px: 4,
          background: 'linear-gradient(113.99deg, #153C7F 12.55%, #000820 87.43%)',
        }}
      >
        <Container maxWidth="md">
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: 'white',
              mb: 6,
              textAlign: 'center',
              fontSize: { xs: '1.75rem', md: '2.5rem' },
            }}
          >
            Frequently Asked Questions
          </Typography>
          <Box>
            {faqs.map((faq, i) => (
              <Accordion
                key={faq.q}
                expanded={expanded === i}
                onChange={handleChange(i)}
                sx={{
                  mb: 2.5,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px !important',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                  },
                  '&:before': { display: 'none' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: 'white' }} />}
                  sx={{ px: { xs: 2, md: 4 }, py: 1.5 }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.02em' }}>
                    {faq.q}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
                  <Typography
                    sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.8, fontSize: '1.05rem' }}
                  >
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: 6, bgcolor: '#f0f4fa' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundImage: `url('/assets/osha/bgdemo.svg'), linear-gradient(113.99deg, #0F4C9C 12.55%, #000820 87.43%)`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: '16px',
              minHeight: '300px',
              py: 5,
              px: 3,
              textAlign: 'center',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 15px 40px rgba(0, 8, 32, 0.12)',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                fontWeight: 800,
                fontSize: { xs: '1.75rem', md: '2.5rem' },
                textTransform: 'uppercase',
              }}
            >
              Ready to Move from Static Binders to a Living Emergency System?
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6, fontSize: '1.1rem', maxWidth: 850 }}>
              From Static Binder to Living Plan. Train Effectively. Respond with Confidence. See how{' '}
              <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> helps SNF operators
              maintain a current, trained, and audit-ready emergency preparedness program — with
              clear accountability and continuous improvement.
            </Typography>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '30px',
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  '&:hover': { bgcolor: '#528a1b' },
                  boxShadow: '0 4px 10px rgba(99, 168, 33, 0.2)',
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
